import { useEffect, useRef, useState } from "react";
import "./About.css";
import useSEO from "../../hooks/useSEO";

const PROFILE = {
  name: "Amit Surera",
  title: "Officer Analyst @ EMRI",
  subtitle: "Analyst · Educator · Founder @ NextSkillAI.com",
  location: "Gandhinagar / Ahmedabad, Gujarat, India",
  email: "sureraamit0@gmail.com",
  phone: "9016912469",
  summary:
    "Data Analyst with strong experience in MIS reporting, analytics, dashboarding, process automation, and stakeholder reporting. Currently working as Officer Analyst at EMRI and previously worked at TCS, with hands-on experience in Excel, Power BI, SQL, Python, and reporting systems for operational and government-focused programs.",
};

const HERO_CHIPS = [
  "Gujarat, India",
  "MIS & Analytics",
  "Founder, NextSkillAI",
  "Excel · Power BI · SQL · Python",
];

const STATS = [
  {
    value: 5,
    display: "5+",
    label: "Years Experience",
    sub: "Analytics & reporting",
    icon: "💼",
  },
  {
    value: 4,
    display: "4",
    label: "Govt Projects",
    sub: "At EMRI",
    icon: "🏛️",
  },
  {
    value: 55,
    display: "55%",
    label: "Efficiency Gain",
    sub: "Through automation",
    icon: "⚙️",
  },
  {
    value: 34,
    display: "34+",
    label: "District Reach",
    sub: "Operational coverage",
    icon: "🗺️",
  },
];

const EXPERIENCE = [
  {
    role: "Officer Analyst",
    company: "EMRI",
    period: "Dec 2025 — Present",
    location: "Ahmedabad, Gujarat",
    color: "emerald",
    tag: "Current Role",
    points: [
      "Lead end-to-end data management and performance analytics for 1962 Animal Mobile Medical Ambulance Services across Gujarat and Daman & Diu.",
      "Oversee analytics and reporting for 4 major government veterinary projects: KAA, MVD, MVU, and MVU & EM.",
      "Prepare and deliver daily, monthly, quarterly, and annual MIS reports to stakeholders, government authorities, and head office.",
      "Design customized analytical reports based on stakeholder and internal business requirements.",
      "Develop and maintain Employee Performance Index (EPI) for doctors, drivers, and field staff.",
      "Analyze ambulance response time, case type, and geographic demand patterns to improve deployment and emergency response efficiency.",
      "Support strategic decisions by identifying high-demand zones for better ambulance positioning.",
      "Build automated reporting dashboards and Excel-based tools to reduce manual effort and improve reporting turnaround time.",
      "Coordinate with Emergency Response Center for data validation and performance monitoring.",
      "Monitor KPIs such as response time, case closure rate, treatment type, and district-wise performance.",
      "Ensure data accuracy, consistency, and compliance with government reporting standards.",
    ],
  },
  {
    role: "Analyst",
    company: "TCS",
    period: "Dec 2019 — Nov 2025",
    location: "Gandhinagar, Gujarat",
    color: "violet",
    tag: "5+ Years",
    points: [
      "Worked with Excel, PowerPoint, and Nielsen tools including Nielsen Answers Desktop, ITO, and DIPE.",
      "Analyzed reports and ensured high-quality, timely results through small automations and strong report quality checks.",
      "Facilitated direct interaction with clients in Europe and the US, building strong client engagement skills.",
      "Prepared business requirement documentation, managed workloads, allocated tasks, validated reports, resolved issues, and handled client queries.",
      "Built automated Excel solutions using complex formulas, conditions, and report quality frameworks.",
      "Improved AGL workflow efficiency by reducing work time by 55% through Excel automation.",
    ],
  },
  {
    role: "Accountant",
    company: "Mehta Sheth & Associates",
    period: "Aug 2019 — Nov 2019",
    location: "Gandhinagar, Gujarat",
    color: "sky",
    tag: "Earlier Role",
    points: [
      "Streamlined financial record-keeping by accurately recording ledger entries.",
      "Ensured financial compliance through auditing of financial records for accuracy and regulatory adherence.",
      "Supported offshore financial operations by auditing offshore ledger entries for consistency and accuracy.",
    ],
  },
];

const SKILLS_TECH = [
  { name: "Advanced Excel", level: 95, color: "#34d399" },
  { name: "Power Query / Power Pivot", level: 90, color: "#7c6ef7" },
  { name: "Power BI", level: 84, color: "#38bdf8" },
  { name: "SQL", level: 82, color: "#22d3ee" },
  { name: "Python", level: 78, color: "#fbbf24" },
  { name: "Pandas / NumPy", level: 76, color: "#f59e0b" },
  { name: "Matplotlib / Seaborn", level: 72, color: "#f472b6" },
  { name: "Basic VBA", level: 68, color: "#34d399" },
];

const TOOLS = [
  "Excel",
  "Advanced Excel",
  "Pivot Table",
  "Power Query",
  "Power Pivot",
  "Basic VBA",
  "Power BI",
  "SQL",
  "Python",
  "Pandas",
  "NumPy",
  "Matplotlib",
  "Seaborn",
  "HTML",
  "CSS",
  "JavaScript",
  "Java",
  "Nielsen Answers Desktop",
  "ITO",
  "DIPE",
  "MIS Reporting",
  "Report Generation",
  "Data Analysis",
];

const DOMAIN_SKILLS = [
  "Market Research Domain",
  "Data Analysis",
  "Report Generation",
  "Problem Solving",
  "Critical Thinking",
  "Client Communication",
  "Business Reporting",
  "Automation Thinking",
];

const SOFT_SKILLS = [
  "Self-Evaluation",
  "Positive Attitude",
  "Quick Learner",
  "Flexible",
  "Hardworking",
  "Sincerity",
  "Honesty",
  "Critical Thinking",
];

const PROJECTS = [
  {
    title: "1962 Animal Ambulance Analytics System",
    org: "EMRI",
    desc: "Built and managed performance analytics, MIS reporting, KPI tracking, and district-wise operational insights for emergency animal ambulance services across Gujarat and Daman & Diu.",
    tags: ["MIS", "Excel", "KPI", "Govt Reporting"],
    color: "emerald",
  },
  {
    title: "Government Veterinary Project Reporting",
    org: "EMRI",
    desc: "Handled analytics and reporting for KAA, MVD, MVU, and MVU & EM projects with customized reports, stakeholder dashboards, and structured performance monitoring.",
    tags: ["Analytics", "Dashboards", "Govt Projects"],
    color: "sky",
  },
  {
    title: "AGL Excel Workflow Automation",
    org: "TCS",
    desc: "Reduced process time by 55% by automating Excel workflows and improving report validation, quality checks, and team efficiency.",
    tags: ["Excel", "Automation", "55% Gain"],
    color: "violet",
  },
  {
    title: "Client Reporting & Quality Operations",
    org: "TCS",
    desc: "Prepared analytical reports for Europe and US clients while managing validations, issue resolution, workload coordination, and business requirement documentation.",
    tags: ["Reporting", "Client Handling", "Quality"],
    color: "amber",
  },
];

const CERTIFICATIONS = [
  "Microsoft Excel — Excel from Beginner to Advanced",
  "Microsoft Excel — Advanced Excel Formulas & Functions",
  "Excel Power Query Beginner to Advanced",
];

const AWARDS = [
  {
    icon: "🏆",
    title: "On The Spot Award",
    org: "TCS",
    year: "Jun & Nov 2024",
    desc: "Recognized for commitment and contribution in achieving goals.",
  },
  {
    icon: "⭐",
    title: "Special Initiative Award",
    org: "TCS Fit4life",
    year: "Jun 2023",
    desc: "Awarded for active involvement in physical activities and initiative participation.",
  },
  {
    icon: "🤝",
    title: "On The Spot (Team) Award",
    org: "TCS",
    year: "Mar 2022",
    desc: "Recognized for contribution in achieving team goals.",
  },
  {
    icon: "🌟",
    title: "Star of the Month Award",
    org: "TCS",
    year: "Apr 2021",
    desc: "Recognized for outstanding contribution to the organization.",
  },
];

const EDUCATION = [
  {
    year: "2019",
    degree: "B.Com",
    institute: "Ashvinbhai A. Patel Commerce College",
    university: "Kadi Sarva Vishwavidyalaya",
    score: "74.6%",
  },
  {
    year: "2016",
    degree: "Class XII — CBSE",
    institute: "Kendriya Vidyalaya (AFS) Salua",
    university: "West Bengal",
    score: "78.6%",
  },
  {
    year: "2014",
    degree: "Class X — CBSE",
    institute: "Air Force School Jodhpur",
    university: "Rajasthan",
    score: "6.2 / 10",
  },
];

function useScrollReveal() {
  useEffect(() => {
    const page = document.querySelector(".about-page");
    if (page) page.classList.add("js-reveal");

    const elements = document.querySelectorAll(".about-page .reveal");

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("active");
          }
        });
      },
      { threshold: 0.08, rootMargin: "0px 0px -8% 0px" }
    );

    elements.forEach((element) => observer.observe(element));

    return () => {
      observer.disconnect();
      if (page) page.classList.remove("js-reveal");
    };
  }, []);
}

function SkillBar({ skill, index }) {
  const [width, setWidth] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => setWidth(skill.level), index * 90);
          observer.disconnect();
        }
      },
      { threshold: 0.35 }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [index, skill.level]);

  return (
    <div className="skill-bar-item" ref={ref}>
      <div className="skill-bar-header">
        <span className="skill-bar-name">{skill.name}</span>
        <span className="skill-bar-pct">{skill.level}%</span>
      </div>

      <div className="skill-bar-track">
        <div
          className="skill-bar-fill"
          style={{
            width: `${width}%`,
            background: skill.color,
            boxShadow: `0 0 14px ${skill.color}55`,
          }}
        />
      </div>
    </div>
  );
}

function StatCard({ stat, index }) {
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          let current = 0;
          const duration = 1400;
          const increment = stat.value / (duration / 16);

          const animate = () => {
            current = Math.min(current + increment, stat.value);

            if (stat.display.includes("%")) {
              element.textContent = `${Math.floor(current)}%`;
            } else if (stat.display.includes("+")) {
              element.textContent = `${Math.floor(current)}+`;
            } else {
              element.textContent = `${Math.floor(current)}`;
            }

            if (current < stat.value) {
              requestAnimationFrame(animate);
            } else {
              element.textContent = stat.display;
            }
          };

          requestAnimationFrame(animate);
        }
      },
      { threshold: 0.45 }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [stat]);

  return (
    <div className="stat-card reveal" style={{ "--delay": `${index * 0.08}s` }}>
      <div className="stat-glow" aria-hidden="true" />
      <div className="stat-icon" aria-hidden="true">
        {stat.icon}
      </div>
      <div className="stat-value" ref={ref}>
        {stat.display}
      </div>
      <div className="stat-label">{stat.label}</div>
      <div className="stat-sub">{stat.sub}</div>
    </div>
  );
}

function About() {
  const glowRef = useRef(null);
  useScrollReveal();

  useSEO({
    title: "About — Amit Surera | Data Analyst & Founder of NextSkillAI",
    description:
      "Learn about Amit Surera, Officer Analyst at EMRI, former TCS Analyst, and Founder of NextSkillAI.com — a free data analytics learning platform built on practical, structured education.",
    keywords:
      "Amit Surera, NextSkillAI founder, data analyst India, Excel expert, Power BI analyst, MIS reporting, EMRI analyst, TCS analyst, data analytics educator, learn data skills",
    canonical: "/about",
    ogType: "profile",
    schema: {
      "@context": "https://schema.org",
      "@type": "ProfilePage",
      name: "About Amit Surera — NextSkillAI",
      url: "https://nextskillai.com/about",
      mainEntity: {
        "@type": "Person",
        name: "Amit Surera",
        jobTitle: "Officer Analyst & Founder",
        worksFor: [
          { "@type": "Organization", name: "EMRI" },
          { "@type": "Organization", name: "NextSkillAI" },
        ],
        knowsAbout: [
          "Excel",
          "Power BI",
          "SQL",
          "Python",
          "Data Analytics",
          "MIS Reporting",
        ],
        url: "https://nextskillai.com/about",
        email: "sureraamit0@gmail.com",
        address: {
          "@type": "PostalAddress",
          addressLocality: "Ahmedabad",
          addressRegion: "Gujarat",
          addressCountry: "IN",
        },
      },
    },
  });

  useEffect(() => {
    const glow = glowRef.current;
    if (!glow) return;

    const handleMove = (e) => {
      glow.style.left = `${e.clientX}px`;
      glow.style.top = `${e.clientY}px`;
    };

    window.addEventListener("mousemove", handleMove, { passive: true });

    return () => window.removeEventListener("mousemove", handleMove);
  }, []);

  return (
    <div className="about-page">
      <div className="cursor-glow" ref={glowRef} aria-hidden="true" />

      <section className="about-hero" aria-label="Profile hero">
        <div className="hero-grid" aria-hidden="true" />

        <div className="hero-inner">
          <div className="hero-left reveal">
            <div className="hero-eyebrow">
              <span className="eyebrow-dot" aria-hidden="true" />
              Ultra Premium About Profile
            </div>

            <h1 className="hero-title">
              Hi, I&apos;m <span className="name-gradient">{PROFILE.name}</span>
            </h1>

            <h2 className="hero-subtitle">
              Turning Reports, Data, and Operations Into{" "}
              <span className="text-gradient">Clear Decision Systems</span>
            </h2>

            <p className="hero-desc">{PROFILE.summary}</p>

            <div className="hero-highlights">
              <div className="hero-highlight-card">
                <span className="highlight-label">Current Role</span>
                <span className="highlight-value">{PROFILE.title}</span>
              </div>
              <div className="hero-highlight-card">
                <span className="highlight-label">Location</span>
                <span className="highlight-value">{PROFILE.location}</span>
              </div>
            </div>

            <div className="hero-chips" role="list" aria-label="Quick facts">
              {HERO_CHIPS.map((chip) => (
                <span key={chip} className="hero-chip chip-v" role="listitem">
                  {chip}
                </span>
              ))}
            </div>

            <div className="hero-actions">
              <a href={`mailto:${PROFILE.email}`} className="btn-primary">
                Contact Me
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  aria-hidden="true"
                >
                  <path d="M3 8h10M8.5 3.5L13 8l-4.5 4.5" />
                </svg>
              </a>

              <a
                href="https://nextskillai.com"
                target="_blank"
                rel="noreferrer"
                className="btn-ghost"
              >
                Visit NextSkillAI
              </a>
            </div>
          </div>

          <div className="hero-right reveal">
            <div className="profile-card" aria-label="Profile summary card">
              <div className="profile-ring" aria-hidden="true" />

              <div className="profile-avatar" aria-label={PROFILE.name}>
                <div className="avatar-initials">AS</div>
              </div>

              <h3 className="profile-name">{PROFILE.name}</h3>
              <p className="profile-role">{PROFILE.title}</p>
              <p className="profile-sub">{PROFILE.subtitle}</p>

              <div className="profile-stats-row" aria-label="Key stats">
                {[
                  ["5+", "Years"],
                  ["4", "Projects"],
                  ["55%", "Boost"],
                ].map(([value, label]) => (
                  <div className="pstat" key={label}>
                    <span className="pstat-val">{value}</span>
                    <span className="pstat-lbl">{label}</span>
                  </div>
                ))}
              </div>

              <div className="profile-tags" aria-label="Core skills">
                <span className="ptag ptag-excel">Excel</span>
                <span className="ptag ptag-powerbi">Power BI</span>
                <span className="ptag ptag-sql">SQL</span>
                <span className="ptag ptag-python">Python</span>
              </div>

              <div className="profile-contact-list">
                <div className="profile-contact-item">
                  <span className="profile-contact-label">Email</span>
                  <a href={`mailto:${PROFILE.email}`}>{PROFILE.email}</a>
                </div>
                <div className="profile-contact-item">
                  <span className="profile-contact-label">Phone</span>
                  <a href={`tel:${PROFILE.phone}`}>{PROFILE.phone}</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="stats-section" aria-label="Career statistics">
        <div className="sec-inner">
          <div className="stats-grid">
            {STATS.map((stat, i) => (
              <StatCard key={stat.label} stat={stat} index={i} />
            ))}
          </div>
        </div>
      </section>

      <section className="about-me-section" aria-label="About me details">
        <div className="sec-inner">
          <div className="about-me-grid">
            <div className="reveal">
              <div className="section-label">
                <span className="label-dot" aria-hidden="true" />
                About Me
              </div>

              <h2 className="section-heading">
                Analyst. Builder.
                <br />
                <span className="text-gradient">Practical Problem Solver.</span>
              </h2>

              <div className="about-card">
                <h3 className="about-card-title">Professional Overview</h3>

                <p className="about-copy">
                  I currently work as <strong>Officer Analyst at EMRI</strong>,
                  where I lead analytics, MIS reporting, KPI tracking, and data
                  operations for animal ambulance and veterinary service programs
                  across Gujarat and Daman &amp; Diu.
                </p>

                <p className="about-copy">
                  Before that, I worked for more than five years at{" "}
                  <strong>TCS</strong>, supporting report analysis, quality
                  checks, client communication, and Excel-based automation for
                  global stakeholders, including clients in Europe and the US.
                </p>

                <p className="about-copy">
                  My core strength is taking operational work that feels heavy,
                  repetitive, or messy and turning it into structured systems,
                  clean reports, automated workflows, and decision-ready
                  dashboards.
                </p>

                <p className="about-copy about-copy-last">
                  Alongside my professional work, I am also building{" "}
                  <strong>NextSkillAI.com</strong> to help learners grow with
                  practical, free, and structured data education.
                </p>
              </div>

              <div className="about-card about-card-gap">
                <h3 className="about-card-title">Domain Strengths</h3>
                <div className="tools-grid">
                  {DOMAIN_SKILLS.map((item) => (
                    <span key={item} className="tool-chip">
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="reveal">
              <div className="about-card">
                <h3 className="about-card-title">Technical Proficiency</h3>
                {SKILLS_TECH.map((skill, i) => (
                  <SkillBar key={skill.name} skill={skill} index={i} />
                ))}
              </div>

              <div className="about-card about-card-gap">
                <h3 className="about-card-title">Soft Skills</h3>
                <div className="soft-skills-grid">
                  {SOFT_SKILLS.map((skill) => (
                    <div className="soft-skill-row" key={skill}>
                      <span className="ss-dot" aria-hidden="true" />
                      <span className="ss-name">{skill}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="about-card tools-card reveal">
            <h3 className="about-card-title">Tools &amp; Technologies</h3>
            <div className="tools-grid">
              {TOOLS.map((tool) => (
                <span key={tool} className="tool-chip">
                  {tool}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="exp-section" aria-label="Work experience">
        <div className="sec-inner">
          <div className="section-label center">
            <span className="label-dot" aria-hidden="true" />
            Work Experience
          </div>

          <h2 className="section-heading center reveal">
            Professional Journey
          </h2>

          <div className="exp-list">
            {EXPERIENCE.map((job, i) => (
              <div
                key={`${job.company}-${job.role}`}
                className="exp-card reveal"
                style={{ "--delay": `${i * 0.1}s` }}
              >
                <div className="exp-card-left">
                  <span className={`exp-tag exp-tag-${job.color}`}>
                    {job.tag}
                  </span>
                  <div className="exp-period">{job.period}</div>
                  <div className="exp-location">{job.location}</div>
                </div>

                <div className="exp-card-body">
                  <div className="exp-header">
                    <h3 className="exp-role">{job.role}</h3>
                    <div className="exp-company">{job.company}</div>
                  </div>

                  <ul className="exp-points">
                    {job.points.map((point, pointIndex) => (
                      <li key={pointIndex} className="exp-point">
                        <span
                          className={`point-dot point-dot-${job.color}`}
                          aria-hidden="true"
                        />
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="projects-section" aria-label="Key projects">
        <div className="sec-inner">
          <div className="section-label center">
            <span className="label-dot" aria-hidden="true" />
            Key Projects
          </div>

          <h2 className="section-heading center reveal">Impact Work</h2>

          <div className="projects-grid">
            {PROJECTS.map((project, i) => (
              <div
                key={project.title}
                className="project-card reveal"
                style={{ "--delay": `${i * 0.08}s` }}
              >
                <div
                  className={`proj-accent proj-accent-${project.color}`}
                  aria-hidden="true"
                />
                <div className="proj-org">{project.org}</div>
                <h3 className="proj-title">{project.title}</h3>
                <p className="proj-desc">{project.desc}</p>

                <div className="proj-tags">
                  {project.tags.map((tag) => (
                    <span
                      key={tag}
                      className={`proj-tag proj-tag-${project.color}`}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section
        className="awards-section"
        aria-label="Awards, certifications and education"
      >
        <div className="sec-inner">
          <div className="awards-certs-grid">
            <div>
              <div className="section-label">
                <span className="label-dot" aria-hidden="true" />
                Awards
              </div>

              <h2 className="section-heading reveal">Recognition</h2>

              <div className="awards-list">
                {AWARDS.map((award, i) => (
                  <div
                    key={award.title}
                    className="award-card reveal"
                    style={{ "--delay": `${i * 0.08}s` }}
                  >
                    <div className="award-icon" aria-hidden="true">
                      {award.icon}
                    </div>

                    <div>
                      <div className="award-header">
                        <h3 className="award-title">{award.title}</h3>
                        <span className="award-year">{award.year}</span>
                      </div>
                      <div className="award-org">{award.org}</div>
                      <div className="award-desc">{award.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="section-label">
                <span className="label-dot" aria-hidden="true" />
                Certifications
              </div>

              <h2 className="section-heading reveal">Credentials</h2>

              <div className="certs-list">
                {CERTIFICATIONS.map((cert, i) => (
                  <div
                    key={cert}
                    className="cert-card reveal"
                    style={{ "--delay": `${i * 0.08}s` }}
                  >
                    <div className="cert-icon" aria-hidden="true">
                      ★
                    </div>
                    <div className="cert-text">{cert}</div>
                  </div>
                ))}
              </div>

              <div className="edu-label">Education</div>

              <div className="edu-list">
                {EDUCATION.map((item, i) => (
                  <div
                    key={`${item.year}-${item.degree}`}
                    className="edu-card reveal"
                    style={{ "--delay": `${i * 0.08}s` }}
                  >
                    <div className="edu-year">{item.year}</div>

                    <div>
                      <div className="edu-degree">{item.degree}</div>
                      <div className="edu-inst">{item.institute}</div>
                      <div className="edu-uni">{item.university}</div>
                      <span className="edu-score">{item.score}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mission-section" aria-label="NextSkillAI mission">
        <div className="sec-inner">
          <div className="mission-card reveal">
            <div className="mission-glow" aria-hidden="true" />

            <div className="mission-inner">
              <div className="mission-eyebrow">
                <span className="label-dot" aria-hidden="true" />
                NextSkillAI Mission
              </div>

              <h2 className="mission-heading">
                Free. Structured.
                <br />
                <span className="text-gradient">Practical Learning.</span>
              </h2>

              <p className="mission-para">
                I am building NextSkillAI.com to make data learning simpler,
                more practical, and more useful for real careers. The goal is to
                give learners a clear path in Excel, SQL, Python, Power BI,
                analytics, and job-ready skills — without unnecessary confusion
                or clutter.
              </p>

              <div className="mission-values">
                {[
                  ["🆓", "100% Free"],
                  ["📘", "Structured Paths"],
                  ["💼", "Career Focused"],
                  ["🌍", "Accessible to All"],
                ].map(([icon, label]) => (
                  <div className="mv-item" key={label}>
                    <span className="mv-icon" aria-hidden="true">
                      {icon}
                    </span>
                    <span className="mv-label">{label}</span>
                  </div>
                ))}
              </div>

              <div className="mission-actions">
                <a
                  href="https://nextskillai.com"
                  target="_blank"
                  rel="noreferrer"
                  className="btn-primary"
                >
                  Visit NextSkillAI
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    aria-hidden="true"
                  >
                    <path d="M3 8h10M8.5 3.5L13 8l-4.5 4.5" />
                  </svg>
                </a>

                <a href={`mailto:${PROFILE.email}`} className="btn-ghost">
                  Email Me
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default About;